import React, { CSSProperties, FC, ReactElement, useState } from 'react';
import { useSettings } from '../../../../../hooks/useSettings';
import { useTranslations } from '../../../../../hooks/useTranslations';
import { NotificationPreferences } from '../../../../../../../__generated__/loma';
import TextLink from '../../../../ui/TextLink/TextLink';
import mustache from 'mustache';
import MustacheNestedRender from '../../../../ui/MustacheNestedRender';
import { useTheme } from '../../../../../hooks/useTheme';
import { useGlobalSettings } from '../../../../../hooks/useGlobalSettings';

const templateLinkContext = (href: string, style?: CSSProperties) => {
  const ALink: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <TextLink
      key={href}
      linkStyle={{
        sDefault: {
          color: style?.color,
          fontSize: 'inherit',
          lineHeight: 'inherit',
        },
      }}
      href={href || ''}
    >
      {children}
    </TextLink>
  );
  return ALink;
};

export interface NotificationCenterDisclaimerTextProps {
  notificationChannel: string;
  notificationPreferencesState?: NotificationPreferences;
  consumerPreferencesOptinEnabled?: boolean;
}

export interface DisclaimerChannelProps {
  channelTextEnabled: boolean | null;
  disclaimerText: string;
  disclaimerTextLinkLabel: string;
}

export enum Channels {
  SMS = 'sms',
  EMAIL = 'email',
  HIPAA_SMS = 'hipaa_sms',
  HIPAA_EMAIL = 'hipaa_email',
  APP_PUSH = 'app_push',
  LINE = 'line',
}

export enum PreferenceLevels {
  TRACKING = 'TRACKING',
  ORDER = 'ORDER',
  RETAILER = 'RETAILER',
  ORDER_ONLY = 'ORDER_ONLY',
}

const NotificationCenterDisclaimerText: FC<
  NotificationCenterDisclaimerTextProps
> = ({
  notificationChannel,
  notificationPreferencesState,
  consumerPreferencesOptinEnabled,
}) => {
  const {
    captiontextDefaultNone,
    linksNoneDefault,
    linksTrackingDefault: linkStyle,
    linksTrackingHover: linkHoverStyle,
  } = useTheme();

  const [disclaimerTextVisible, setDisclaimerTextVisible] = useState(false);

  const { notificationCenter } = useSettings();
  const {
    enableEmailDisclaimerText = null,
    toggleDisclaimerText = false,
    marketingOptInEnabled = false,
    marketingOptInPrivacyLink,
    marketingOptInTermsLink,
  } = notificationCenter || {};

  const {
    notificationCenterSmsDisclaimerText = '',
    notificationCenterSmsDisclaimerTextMarketing = '',
    notificationCenterSmsDisclaimerLinkLabel = '',
    notificationCenterSmsDisclaimerTextRetailerLevel = '',
    notificationCenterEmailDisclaimerText = '',
    notificationCenterEmailDisclaimerLinkLabel = '',
    notificationCenterEmailDisclaimerTextRetailerLevel = '',
    notificationCenterLineDisclaimerText = '',
    notificationCenterLineDisclaimerLinkLabel = '',
    notificationCenterSublimeDisclaimerCopy = '',
  } = useTranslations();

  const smsDisclaimerText = marketingOptInEnabled
    ? notificationCenterSmsDisclaimerTextMarketing
    : notificationCenterSmsDisclaimerText;

  const { retailerLevelOptInEnabled, retailerName } = useGlobalSettings();

  const { preferenceLevel } = notificationPreferencesState || {};
  const retailerLevel =
    retailerLevelOptInEnabled &&
    (preferenceLevel === PreferenceLevels.RETAILER ||
      preferenceLevel === PreferenceLevels.ORDER);

  const smsDisclaimerChannelProps: DisclaimerChannelProps = {
    channelTextEnabled: true,
    disclaimerText: retailerLevel
      ? notificationCenterSmsDisclaimerTextRetailerLevel || ''
      : smsDisclaimerText || '',
    disclaimerTextLinkLabel: notificationCenterSmsDisclaimerLinkLabel || '',
  };

  const emailDisclaimerChannelProps: DisclaimerChannelProps = {
    channelTextEnabled: enableEmailDisclaimerText,
    disclaimerText: retailerLevel
      ? notificationCenterEmailDisclaimerTextRetailerLevel || ''
      : notificationCenterEmailDisclaimerText || '',
    disclaimerTextLinkLabel: notificationCenterEmailDisclaimerLinkLabel || '',
  };

  // TODO: Add retailer level disclaimer text
  const lineDisclaimerChannelProps: DisclaimerChannelProps = {
    channelTextEnabled: true,
    disclaimerText: notificationCenterLineDisclaimerText || '',
    disclaimerTextLinkLabel: notificationCenterLineDisclaimerLinkLabel || '',
  };

  const appleWalletDisclaimerChannelProps: DisclaimerChannelProps = {
    channelTextEnabled: false,
    disclaimerText: '',
    disclaimerTextLinkLabel: '',
  };

  const disclaimerChannelProps: {
    [notificationChannel: string]: DisclaimerChannelProps;
  } = {
    [Channels.SMS]: smsDisclaimerChannelProps,
    [Channels.HIPAA_SMS]: smsDisclaimerChannelProps,
    [Channels.EMAIL]: emailDisclaimerChannelProps,
    [Channels.HIPAA_EMAIL]: emailDisclaimerChannelProps,
    [Channels.APP_PUSH]: appleWalletDisclaimerChannelProps,
    [Channels.LINE]: lineDisclaimerChannelProps,
  };

  if (!disclaimerChannelProps[notificationChannel]) return null;

  const {
    channelTextEnabled = false,
    disclaimerText = '',
    disclaimerTextLinkLabel = '',
  }: DisclaimerChannelProps = disclaimerChannelProps[notificationChannel];

  // if flag isn't set (i.e., null | undefined), set default to true
  const disclaimerTextEnabled =
    typeof channelTextEnabled === 'boolean' ? channelTextEnabled : true;

  if (!disclaimerText || !disclaimerTextEnabled) return null;

  return (
    <>
      {!toggleDisclaimerText && !consumerPreferencesOptinEnabled && (
        <TextLink
          onClick={(): void => setDisclaimerTextVisible(!disclaimerTextVisible)}
          linkStyle={{ sDefault: linkStyle, sHover: linkHoverStyle }}
        >
          {disclaimerTextLinkLabel}
        </TextLink>
      )}
      {(toggleDisclaimerText || disclaimerTextVisible) && (
        <p
          style={{
            fontSize: 'inherit',
            margin: 0,
            padding:
              toggleDisclaimerText || consumerPreferencesOptinEnabled
                ? 0
                : '1rem 0 0 0',
            textAlign: 'left',
            ...captiontextDefaultNone,
          }}
        >
          {consumerPreferencesOptinEnabled ? (
            <MustacheNestedRender
              template={mustache.parse(
                notificationCenterSublimeDisclaimerCopy ?? '',
              )}
              context={
                new mustache.Context({
                  termsLink: () =>
                    templateLinkContext(
                      'https://corp.narvar.com/profile-terms-of-service',
                      linksNoneDefault,
                    ),
                  privacyLink: () =>
                    templateLinkContext(
                      'https://corp.narvar.com/profile-privacy-policy',
                      linksNoneDefault,
                    ),
                })
              }
            />
          ) : (
            <MustacheNestedRender
              template={mustache.parse(disclaimerText ?? '')}
              context={
                new mustache.Context({
                  termsLink: () =>
                    templateLinkContext(
                      marketingOptInTermsLink ?? '',
                      linksNoneDefault,
                    ),
                  privacyLink: () =>
                    templateLinkContext(
                      marketingOptInPrivacyLink ?? '',
                      linksNoneDefault,
                    ),
                  retailerName,
                })
              }
            />
          )}
        </p>
      )}
    </>
  );
};

export default NotificationCenterDisclaimerText;
